import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import useLocalStorage from '@msales/shared/hooks/useLocalStorage';
import useStoredCart from '../hooks/useStoredCart';
import { computePrice } from '@msales/shared/utils/price';
import moment from 'moment';
import message from 'antd/es/message';
import { cartDebouncer } from '../utils/cartDebounce';
import { PRICE_DISPLAY_MODES } from '../config/productPricing';
import { calculateCartTaxSummary } from '../utils/cartTaxSummary';
import { useAuthContext } from './authContext';
import { useClientConfig } from './clientConfigContext';
import {
  formatDecimalAmount,
  getAmountEpsilon,
  getConfiguredAmountDecimals,
} from '../utils/moneyFormat';

export const Context = createContext({});
const CartActionsContext = createContext({});

const normalizeValue = (value) => `${value ?? ''}`.trim().toUpperCase();

const matchesOrganizationPricingRule = (rule, organization) => {
  if (!rule || !organization) return false;

  const organizationId = organization?.id ?? organization;
  const organizationName =
    organization?.name ?? (typeof organization === 'string' ? organization : '');
  const organizationIds = (rule.organizationIds || []).map((id) => `${id}`);
  const organizationNames = (rule.organizationNames || [])
    .map(normalizeValue)
    .filter(Boolean);

  return (
    organizationIds.includes(`${organizationId}`) ||
    organizationNames.some((name) =>
      normalizeValue(organizationName).includes(name),
    )
  );
};

const hasValue = (value) => value !== undefined && value !== null && value !== '';

const getTariffGroup = (...items) => {
  for (const item of items) {
    const tariffGroup = item?.tarifnaGrupa ?? item?.tarifnagrupa;
    if (hasValue(tariffGroup)) return tariffGroup;
  }

  return undefined;
};

const applyCompoundDiscounts = (
  price,
  discount1 = 0,
  discount2 = 0,
  discount3 = 0,
) => {
  let value = Number(price || 0);

  [discount1, discount2, discount3].forEach((discount) => {
    const rate = Number(discount || 0);
    if (rate > 0) value *= 1 - rate / 100;
  });

  return value;
};

export const Provider = ({ children }) => {
  const { authUser } = useAuthContext();
  const authOrganization = authUser?.organization;
  const { getValue } = useClientConfig();
  const [cart, setCart] = useStoredCart({
    compact: getValue('orders.compactCartStorage', false),
  });
  const [ocrPreviewItems, setOcrPreviewItems] = useLocalStorage(
    'ocrPreviewItems',
    [],
  );
  const [comment, setComment] = useState(null);
  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState(
    getDateAfterTwoBusinessDays(),
  );
  const [delivery, setDelivery] = useState(true);
  const [deliveryLocation, setDeliveryLocation] = useState(null);
  const [discountClass, setDiscountClass] = useLocalStorage(
    'discountClass',
    null,
  );
  const [giftClass, setGiftClass] = useLocalStorage('giftClass', null);
  const [giftClasses, setGiftClasses] = useLocalStorage('giftClasses', null);
  const [retail, setRetail] = useState(false);
  const [orderType, setStoredOrderType] = useLocalStorage('doc', null);
  const orderTypeCode = orderType?.code;
  const [tobaccoTargetGroup, setTobaccoTargetGroup] = useLocalStorage(
    'tobaccoTargetGroup',
    null,
  );
  const [page, setPage] = useState('3');
  const [result, setResult] = useState(null);
  const [KOimages, setKOimages] = useState({});
  const [organization, setOrganization] = useLocalStorage(
    'selectedOrganization',
    null,
  );
  const [paymentOption, setPaymentOption] = useLocalStorage(
    'paymentOption',
    getValue('orders.defaultPaymentOption', 'ZIR'),
  );
  const [mobileCreatedAt, setMobileCreatedAt] = useLocalStorage(
    'mobileCreatedAt',
    null,
  );

  const [isRapidUpdate, setIsRapidUpdate] = useState(false);
  const rapidUpdateTimer = useRef(null);
  const cleanupTimer = useCallback(() => {
    if (rapidUpdateTimer.current) {
      clearTimeout(rapidUpdateTimer.current);
      rapidUpdateTimer.current = null;
    }
  }, []);
  const updateCartLastUpdate = useCallback(() => {
    cartDebouncer.queueUpdate('cart_timestamp', {
      action: 'update_timestamp',
      timestamp: Date.now(),
    });
  }, []);

  const setOrderType = useCallback(
    (nextOrderType) => {
      setStoredOrderType(nextOrderType);
      setTobaccoTargetGroup(null);
    },
    [setStoredOrderType, setTobaccoTargetGroup],
  );

  useEffect(() => {
    const unsubscribe = cartDebouncer.subscribe((updates) => {
      updates.forEach(([productId, updateData]) => {
        if (productId === 'cart_timestamp') {
          localStorage.setItem(
            'cart_last_update',
            updateData.timestamp.toString(),
          );
        }
      });
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    cleanupTimer();

    if (cart.length > 0) {
      setIsRapidUpdate(true);
      rapidUpdateTimer.current = setTimeout(() => {
        setIsRapidUpdate(false);
        cartDebouncer.flush();
        rapidUpdateTimer.current = null;
      }, 1000);
    } else {
      setIsRapidUpdate(false);
    }

    return cleanupTimer;
  }, [cart.length, cleanupTimer]);

  const addProduct = useCallback((product, quantity, discount2, comment) => {
    updateCartLastUpdate();

    setCart((c) => {
      const cartProduct = { ...product, quantity };
      if (discount2) cartProduct.discount2 = discount2;
      if (comment) cartProduct.comment = comment;
      return [cartProduct, ...c];
    });
  }, [setCart, updateCartLastUpdate]);

  const limitQuantityToAvailableStock = useCallback((product, quantity) => {
    if (!getValue('orders.limitQuantityToAvailableStock', false)) {
      return quantity;
    }

    const stockQuantity = product?.stockQuantity;
    if (stockQuantity === undefined || stockQuantity === null || stockQuantity === '') {
      return quantity;
    }

    const availableStock = Number(stockQuantity);
    if (!Number.isFinite(availableStock)) return quantity;

    const maximumQuantity = Math.max(0, availableStock);
    if (Number(quantity) > maximumQuantity) {
      message.warning(
        `Dostupna zaliha artikla je ${maximumQuantity}. Količina je postavljena na ${maximumQuantity}.`,
      );
    }

    return Math.min(quantity, maximumQuantity);
  }, [getValue]);

  const removeProduct = useCallback((product) => {
    updateCartLastUpdate();

    setCart((c) => [...c.filter((p) => product.id !== p.id)]);
    setOcrPreviewItems((c) => [...c.filter((p) => product.id !== p.id)]);
  }, [setCart, setOcrPreviewItems, updateCartLastUpdate]);

  const editQuantity = useCallback((product, newQuantity, discount2) => {
    const limitedQuantity = limitQuantityToAvailableStock(
      product,
      newQuantity,
    );
    updateCartLastUpdate();

    setOcrPreviewItems((c) => {
      const i = c.findIndex((p) => p.id === product.id);
      if (i === -1) return c;
      return c.map((item, index) => index === i ? { ...item, quantity: limitedQuantity } : item);
    });
    setCart((c) => {
      const i = c.findIndex((p) => p.id === product.id);
      if (i === -1) return c;
      const item = { ...c[i], quantity: limitedQuantity };
      if (getValue('productPricing.preserveTariffGroup', false)) {
        const tariffGroup = getTariffGroup(product, c[i]);
        if (hasValue(tariffGroup)) {
          item.tarifnaGrupa = tariffGroup;
          item.tarifnagrupa = tariffGroup;
        }
      }
      if (discount2) item.discount2 = discount2;
      if (product.packageQuantity !== undefined) {
        item.packageQuantity = product.packageQuantity;
      } else {
        delete item.packageQuantity;
      }
      if (product.comment !== undefined) {
        item.comment = product.comment;
      }
      return c.map((current, index) => index === i ? item : current);
    });
  }, [getValue, limitQuantityToAvailableStock, setCart, setOcrPreviewItems, updateCartLastUpdate]);

  const updateCartItemComment = useCallback((productId, comment) => {
    updateCartLastUpdate();

    setCart((c) => {
      const i = c.findIndex((p) => p.id === productId);
      if (i === -1) return c;
      return c.map((item, index) => index === i ? { ...item, comment: comment || '' } : item);
    });
  }, [setCart, updateCartLastUpdate]);

  const clearCart = useCallback(() => {
    setCart([]);
    setOcrPreviewItems([]);
    setComment(null);
    setRequestedDeliveryDate(getDateAfterTwoBusinessDays());
    setDelivery(true);
    setDeliveryLocation(null);
    setRetail(false);
    setKOimages({});
    setPaymentOption(getValue('orders.defaultPaymentOption', 'ZIR'));
    setMobileCreatedAt(null);
  }, [getValue, setCart, setOcrPreviewItems, setPaymentOption, setMobileCreatedAt]);

  const cartLookupMap = useMemo(() => {
    if (cart.length === 0) return new Map();

    const lookupMap = new Map();
    cart.forEach((item) => {
      lookupMap.set(item.id, item);
    });
    return lookupMap;
  }, [cart]);

  const inCart = useCallback(
    (product) => {
      if (!product || !product.id || cart.length === 0) return null;

      const found = cartLookupMap.get(product.id);
      return found ? { ...found } : null;
    },
    [cart.length, cartLookupMap],
  );

  const getProductQuantity = useCallback(
    (product) => {
      if (!product || !product.id) return undefined;

      const found = cartLookupMap.get(product.id);
      return found ? found.quantity : undefined;
    },
    [cartLookupMap],
  );

  const cartPriceDisplayMode = useMemo(() => {
    const productPricing = getValue('productPricing', {});
    const organizationOverrides = productPricing.organizationOverrides || [];
    const matchedOverride = organizationOverrides.find((rule) =>
      [organization, authOrganization].some((org) =>
        matchesOrganizationPricingRule(rule, org),
      ),
    );

    return (
      matchedOverride?.displayMode ||
      productPricing.defaultDisplayMode ||
      PRICE_DISPLAY_MODES.STANDARD
    );
  }, [authOrganization, getValue, organization]);

  const cartUsesGrossPrices =
    getValue('orders.useGrossCartPrices', false) &&
    cartPriceDisplayMode === PRICE_DISPLAY_MODES.RETAIL_WITH_PDV;
  const orderAmountDecimals = getConfiguredAmountDecimals(getValue);
  const orderAmountEpsilon = getAmountEpsilon(orderAmountDecimals);

  const getCartItemBasePrice = useCallback(
    (item) => {
      if (getValue('productPricing.strategy') === 'price-inquiry') {
        if (cartUsesGrossPrices) {
          return Number(
            item?.cenaCenovnikSaPDV ??
              item?.retailPrice ??
              item?.wholesalePrice ??
              item?.price ??
              0,
          );
        }

        return Number(
          item?.cenaCenovnikBezPDV ??
            item?.wholesalePrice ??
            item?.price ??
            0,
        );
      }

      return Number(item?.wholesalePrice ?? item?.price ?? 0);
    },
    [cartUsesGrossPrices, getValue],
  );

  const getCartItemDiscountedPrice = useCallback(
    (item) => {
      if (getValue('productPricing.strategy') === 'price-inquiry') {
        if (cartUsesGrossPrices) {
          if (
            item?.krajnjaCenaSaPDV !== undefined &&
            item?.krajnjaCenaSaPDV !== null
          ) {
            return Number(item.krajnjaCenaSaPDV || 0);
          }

          return applyCompoundDiscounts(
            getCartItemBasePrice(item),
            item?.isHistoryBasedKO ? 0 : item?.discount1,
            item?.discount2,
            item?.discount3,
          );
        }

        if (
          item?.krajnjaCenaBezPDV !== undefined &&
          item?.krajnjaCenaBezPDV !== null
        ) {
          return Number(item.krajnjaCenaBezPDV || 0);
        }

        if (item?.nettoVpc !== undefined && item?.nettoVpc !== null) {
          return Number(item.nettoVpc || 0);
        }

        return applyCompoundDiscounts(
          getCartItemBasePrice(item),
          item?.isHistoryBasedKO ? 0 : item?.discount1,
          item?.discount2,
          item?.discount3,
        );
      }

      return Number(
        computePrice(
          item?.wholesalePrice || 0,
          item?.isHistoryBasedKO ? 0 : item?.discount1 || 0,
          item?.discount2,
          item,
          orderAmountDecimals,
        ),
      );
    },
    [cartUsesGrossPrices, getValue, getCartItemBasePrice, orderAmountDecimals],
  );

  const cartTotal = useMemo(() => {
    if (isRapidUpdate) {
      return formatDecimalAmount(0, orderAmountDecimals);
    }

    const total = cart.reduce(
      (a, b) => a + getCartItemBasePrice(b) * (b.quantity || 0),
      0,
    );
    return formatDecimalAmount(total, orderAmountDecimals);
  }, [cart, getCartItemBasePrice, isRapidUpdate, orderAmountDecimals]);

  const cartQuantity = useMemo(() => {
    return cart.reduce((a, b) => a + (b.quantity || 0), 0);
  }, [cart]);

  const cartDiscountedTotal = useMemo(() => {
    if (isRapidUpdate) {
      return formatDecimalAmount(0, orderAmountDecimals);
    }

    const total = cart.reduce((a, b) => {
      return a + getCartItemDiscountedPrice(b) * (b.quantity || 0);
    }, 0);
    return formatDecimalAmount(total, orderAmountDecimals);
  }, [cart, getCartItemDiscountedPrice, isRapidUpdate, orderAmountDecimals]);

  const cartTaxSummary = useMemo(() => {
    return calculateCartTaxSummary({
      cart,
      cartUsesGrossPrices,
      getCartItemDiscountedPrice,
      precision: orderAmountDecimals,
    });
  }, [cart, cartUsesGrossPrices, getCartItemDiscountedPrice, orderAmountDecimals]);

  const cartWeight = useMemo(() => {
    if (isRapidUpdate) return '0.00';

    const weight = cart.reduce(
      (a, b) => a + (b.weight || 0) * (b.quantity || 0),
      0,
    );
    return weight.toFixed(2);
  }, [cart, isRapidUpdate]);

  const validateCart = useCallback(() => {
    return cart && cart.every((item) => item.quantity && !isNaN(item.quantity));
  }, [cart]);

  const getPageTitle = useCallback(() => {
    if (page === 0) return 'Pregled';
    else if (page === 1) return 'Zaključi';
    else if (page === 2) return 'Potvrda';
  }, [page]);

  const addProductToCart = useCallback((product, quantity, koDiscount) => {
    const limitedQuantity = limitQuantityToAvailableStock(product, quantity);
    const validQuantity = limitedQuantity && limitedQuantity > 0;
    let validKO =
      koDiscount &&
      koDiscount > 0 &&
      koDiscount <= 100 &&
      koDiscount !== product.discount2 &&
      orderTypeCode === 'KO';

    if (validQuantity && !product.quantity) {
      addProduct(product, limitedQuantity, validKO && koDiscount);
    } else if (validQuantity && (product.quantity !== limitedQuantity || validKO)) {
      editQuantity(product, limitedQuantity, validKO && koDiscount);
    } else if (quantity === 0) {
      removeProduct(product);
    }
  }, [limitQuantityToAvailableStock, orderTypeCode, addProduct, editQuantity, removeProduct]);

  // if article is not found because of the wrong barcode, let user insert new barcode and replace the old one
  const addProductToCartWithNewBarcode = useCallback((ocrIndex, product, quantity = 0) => {
    const limitedQuantity = limitQuantityToAvailableStock(product, quantity);
    const validQuantity = limitedQuantity && limitedQuantity > 0;
    let validKO =
      product.discount2 &&
      product.discount2 > 0 &&
      product.discount2 <= 100 &&
      orderTypeCode === 'KO';

    if (validQuantity && !product.quantity) {
      addProduct(product, limitedQuantity, validKO && product.discount2);
    } else if (validQuantity && (product.quantity !== limitedQuantity || validKO))
      editQuantity(product, limitedQuantity, validKO && product.discount2);
    else if (quantity === 0) removeProduct(product);

    setOcrPreviewItems((items) => {
      const updatedItems = [...items];
      updatedItems[ocrIndex] = { ...product, quantity: limitedQuantity };
      return updatedItems;
    });

  }, [limitQuantityToAvailableStock, orderTypeCode, addProduct, editQuantity, removeProduct, setOcrPreviewItems]);

  const updateCartItem = useCallback((product) => {
    updateCartLastUpdate();

    setCart((previous) => {
      const index = previous.findIndex((p) => p.id === product.id);
      if (index === -1) return previous;

      const updatedItem = { ...previous[index], ...product };
      if (getValue('productPricing.preserveTariffGroup', false)) {
        const tariffGroup = getTariffGroup(product, previous[index]);
        if (hasValue(tariffGroup)) {
          updatedItem.tarifnaGrupa = tariffGroup;
          updatedItem.tarifnagrupa = tariffGroup;
        }
      }
      if (Object.keys(updatedItem).every((key) => Object.is(updatedItem[key], previous[index][key]))) {
        return previous;
      }
      const updatedCart = [...previous];
      updatedCart[index] = updatedItem;
      return updatedCart;
    });
  }, [getValue, setCart, updateCartLastUpdate]);

  const cartContext = useMemo(
    () => ({
      page,
      setPage,
      cart,
      setCart,
      addProduct,
      removeProduct,
      editQuantity,
      updateCartItemComment,
      clearCart,
      inCart,
      cartTotal,
      cartQuantity,
      getProductQuantity,
      delivery,
      setDelivery,
      deliveryLocation,
      setDeliveryLocation,
      discountClass,
      setDiscountClass,
      retail,
      setRetail,
      validateCart,
      getPageTitle,
      cartWeight,
      result,
      isRapidUpdate,
      cartLookupMap,
      setResult,
      comment,
      setComment,
      requestedDeliveryDate,
      setRequestedDeliveryDate,
      cartDiscountedTotal,
      cartPriceDisplayMode,
      cartUsesGrossPrices,
      cartTaxSummary,
      orderAmountDecimals,
      orderAmountEpsilon,
      orderType,
      setOrderType,
      tobaccoTargetGroup,
      setTobaccoTargetGroup,
      addProductToCart,
      giftClasses,
      setGiftClasses,
      giftClass,
      setGiftClass,
      ocrPreviewItems,
      setOcrPreviewItems,
      KOimages,
      setKOimages,
      organization,
      setOrganization,
      addProductToCartWithNewBarcode,
      paymentOption,
      setPaymentOption,
      mobileCreatedAt,
      setMobileCreatedAt,
      updateCartItem,
      getCartItemBasePrice,
      getCartItemDiscountedPrice,
    }),
    [
      page,
      cart,
      setCart,
      setDiscountClass,
      setGiftClass,
      setGiftClasses,
      setOcrPreviewItems,
      setOrganization,
      setPaymentOption,
      setTobaccoTargetGroup,
      cartTotal,
      cartQuantity,
      cartWeight,
      cartDiscountedTotal,
      cartPriceDisplayMode,
      cartUsesGrossPrices,
      cartTaxSummary,
      orderAmountDecimals,
      orderAmountEpsilon,
      cartLookupMap,
      delivery,
      deliveryLocation,
      discountClass,
      retail,
      comment,
      requestedDeliveryDate,
      orderType,
      setOrderType,
      tobaccoTargetGroup,
      giftClasses,
      giftClass,
      ocrPreviewItems,
      KOimages,
      organization,
      result,
      isRapidUpdate,
      paymentOption,
      mobileCreatedAt,
      setMobileCreatedAt,
      addProduct,
      removeProduct,
      editQuantity,
      updateCartItemComment,
      clearCart,
      inCart,
      getProductQuantity,
      validateCart,
      getPageTitle,
      addProductToCart,
      addProductToCartWithNewBarcode,
      updateCartItem,
      getCartItemBasePrice,
      getCartItemDiscountedPrice,
    ],
  );

  const actions = useMemo(() => ({
    addProductToCart, updateCartItem, editQuantity, removeProduct, organization, orderType, setOrderType,
  }), [addProductToCart, updateCartItem, editQuantity, removeProduct, organization, orderType, setOrderType]);

  return <CartActionsContext.Provider value={actions}><Context.Provider value={cartContext}>{children}</Context.Provider></CartActionsContext.Provider>;
};

export const { Consumer } = Context;

export const useCartContext = () => useContext(Context);
export const useCartActions = () => useContext(CartActionsContext);

const getDateAfterTwoBusinessDays = () => {
  const today = moment();
  const currentDay = today.isoWeekday();
  const daysToAdd = 2 + 2 * ((currentDay / 4) | 0);
  return today.add(daysToAdd, 'days');
};
